import axios from "axios";
import { useContext, useEffect, useState, useCallback } from "react";
import { Table, DatePicker, Select, Input, Button } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { AuthContext } from "../../context/AuthContext";

dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export default function ViewImproper() {
    const { authData, backUrl } = useContext(AuthContext);

    const [requests, setRequests] = useState([]);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [filters, setFilters] = useState({
        status: null,
        fromDate: null,
        toDate: null,
        employeeName: "",
        employeeCode: "",
    });

    const [pageSize, setPageSize] = useState(50);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const buildQueryParams = useCallback(
        (overrideFilters = null) => {
            const params = new URLSearchParams();
            const f = overrideFilters ?? filters;

            if (f.status) params.set("status", f.status);
            if (f.fromDate) params.set("from_date", f.fromDate.format("YYYY-MM-DD"));
            if (f.toDate) params.set("to_date", f.toDate.format("YYYY-MM-DD"));
            if (f.employeeName?.trim()) params.set("emp_name", f.employeeName.trim());
            if (f.employeeCode?.trim()) params.set("emp_code", f.employeeCode.trim());

            return params.toString();
        },
        [filters]
    );

    const fetchData = useCallback(
        async (overrideFilters = null) => {
            try {
                const queryString = buildQueryParams(overrideFilters);
                const res = await axios.get(
                    `${backUrl}/api/hr/regularization/all-requests/?${queryString}`,
                    {
                        headers: {
                            Authorization: `Bearer ${authData?.access}`,
                        },
                    }
                );
                setRequests(res.data.results || res.data);
            } catch (error) {
                console.error("Error fetching regularization requests:", error.response || error.message);
            }
        },
        [backUrl, authData, buildQueryParams]
    );

    useEffect(() => {
        fetchData();
    }, []);

    const handleSearch = () => {
        fetchData();
    };

    const handleReset = () => {
        const clearedFilters = {
            status: null,
            fromDate: null,
            toDate: null,
            employeeName: "",
            employeeCode: "",
        };
        setFilters(clearedFilters);
        fetchData(clearedFilters);
    };

    const statusColors = {
        PENDING: "#faad14",
        APPROVED: "green",
        REJECTED: "red",
    };

    const getStatusColor = (status) => {
        if (!status) return "#faad14";
        return statusColors[status.toUpperCase()] || "#faad14";
    };

    const columns = [
        {
            title: "Employee Name",
            dataIndex: "employee_name",
            key: "employee_name",
        },
        {
            title: "Employee Code",
            dataIndex: "employee_code",
            key: "employee_code",
        },
        {
            title: "Attendance Date",
            dataIndex: "attendance_date",
            key: "attendance_date",
            render: (val) => (val ? dayjs(val).format("DD/MM/YYYY") : "-"),
        },
        {
            title: "Actual Type",
            key: "type",
            render: (_, record) => (
                (record.first_in_type || record.last_out_type) && (
                    <>
                        {record.first_in_type ? "First In" : "Last Out"}
                    </>
                )
            )
        },
        {
            title: "Actual Mode",
            key: "mode",
            render: (_, record) => (
                (record.first_in_type || record.last_out_type) && (
                    <>
                        {record.first_in_type ? record.actual_first_in_type : record.actual_last_out_type}
                    </>
                )
            )
        },
        {
            title: "Actual Time",
            key: "time",
            render: (_, record) => {
                const value = record.first_in_type
                    ? record.actual_first_in_value
                    : record.actual_last_out_value;
                return value ? dayjs(value).format("DD/MM/YYYY hh:mm A") : "-";
            },
        },
        {
            title: "Updated Type",
            key: "first_in_type",
            render: (_, record) => (
                (record.first_in_type || record.last_out_type) && (
                    <>
                        {record.first_in_type ? "First In" : "Last Out"}
                    </>
                )
            )
        },
        {
            title: "Updated Mode",
            key: "updated_mode",
            render: (_, record) => (
                (record.first_in_type || record.last_out_type) && (
                    <>
                        {record.first_in_type || record.last_out_type}
                    </>
                )
            )
        },
        {
            title: "Updated Time",
            key: "updated_time",
            render: (_, record) => {
                const value = record.first_in_value || record.last_out_value;
                return value ? dayjs(value).format("DD/MM/YYYY hh:mm A") : "-";
            },
        },
        {
            title: "Reason",
            dataIndex: "reason_by_employee",
            key: "reason_by_employee",
            width: 250,
            render: (text) => (
                <div style={{ whiteSpace: "normal", wordWrap: "break-word", maxWidth: "250px" }}>
                    {text || "-"}
                </div>
            ),
        },
        {
            title: "Approver Remarks",
            dataIndex: "remarks_by_approver",
            key: "remarks_by_approver",
            width: 200,
            render: (text) => (
                <div style={{ whiteSpace: "normal", wordWrap: "break-word", maxWidth: "200px" }}>
                    {text || "-"}
                </div>
            ),
        },
        {
            title: "Approved On",
            dataIndex: "approved_on",
            key: "approved_on",
            render: (val) => (val ? dayjs(val).format("DD/MM/YYYY hh:mm A") : "-"),
        },
        {
            title: "Created At",
            dataIndex: "created_at",
            key: "created_at",
            render: (val) => (val ? dayjs(val).format("DD/MM/YYYY hh:mm A") : "-"),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (text) => (
                <span style={{ fontWeight: 500, color: getStatusColor(text) }}>
                    {text
                        ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
                        : "-"}
                </span>
            ),
        },
    ];

    return (
        <>
            <center><h4>Improper Attendance Requests</h4></center>

            {/* Filters */}
            <div
                style={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: "10px",
                    marginBottom: "16px",
                    flexDirection: isMobile ? "column" : "row",
                    alignItems: "center",
                }}
            >
                <Input
                    placeholder="Employee Name"
                    value={filters.employeeName}
                    allowClear
                    style={{ width: isMobile ? "100%" : 180 }}
                    onChange={(e) =>
                        setFilters((prev) => ({ ...prev, employeeName: e.target.value }))
                    }
                    onPressEnter={handleSearch}
                />

                <Input
                    placeholder="Employee Code"
                    value={filters.employeeCode}
                    allowClear
                    style={{ width: isMobile ? "100%" : 160 }}
                    onChange={(e) =>
                        setFilters((prev) => ({ ...prev, employeeCode: e.target.value }))
                    }
                    onPressEnter={handleSearch}
                />

                <DatePicker
                    placeholder="From Date"
                    format="DD/MM/YYYY"
                    value={filters.fromDate}
                    style={{ width: isMobile ? "100%" : 160 }}
                    onChange={(date) => setFilters((prev) => ({ ...prev, fromDate: date }))}
                />

                <DatePicker
                    placeholder="To Date"
                    format="DD/MM/YYYY"
                    value={filters.toDate}
                    style={{ width: isMobile ? "100%" : 160 }}
                    onChange={(date) => setFilters((prev) => ({ ...prev, toDate: date }))}
                />

                <Select
                    placeholder="Status"
                    value={filters.status}
                    style={{ width: isMobile ? "100%" : 160 }}
                    allowClear
                    onChange={(value) => setFilters((prev) => ({ ...prev, status: value ?? null }))}
                >
                    {["PENDING", "APPROVED", "REJECTED"].map((status) => (
                        <Select.Option key={status} value={status}>
                            {status.charAt(0) + status.slice(1).toLowerCase()}
                        </Select.Option>
                    ))}
                </Select>

                <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    onClick={handleSearch}
                    style={{ backgroundColor: "#0e497b", borderColor: "#0e497b" }}
                >
                    Search
                </Button>

                <Button onClick={handleReset}>Clear</Button>
            </div>

            {/* Table */}
            <Table
                columns={columns}
                dataSource={requests.map((item, index) => ({
                    key: index,
                    ...item,
                }))}
                pagination={{
                    pageSize: pageSize,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50", "100"],
                    onShowSizeChange: (current, size) => setPageSize(size),
                }}
                scroll={{ x: true }}
                components={{
                    header: {
                        cell: (props) => (
                            <th
                                {...props}
                                style={{
                                    backgroundColor: "#0e497b",
                                    color: "white",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {props.children}
                            </th>
                        ),
                    },
                }}
                bordered
            />
        </>
    );
}